import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";

import Dropdown from "pages/members/components/Dropdown";
import { fetchMembers, updateMemberAuth } from "shared/api/MemberApi";
import { ModalButton } from "shared/ui/buttons";
import { alertDialog, confirmDialog, confirmLeaveIfDirty, ModalShell } from "shared/ui/feedback";
import { ChipGroup } from "shared/ui/filters";
import { ButtonBar } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

const Div = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f6f6f6;
  padding: 25px 50px;
  height: 100vh;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  background: #ffffff;
  width: 100%;
  height: 65px;
  margin-top: 15px;
  border-radius: 4px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 4px;
  padding: 15px 20px;
`;

const TextInput = styled.input`
  width: 100%;
  height: 35px;
  border-radius: 2px;
  border: 1px solid var(--admin-gray-stroke, #e8e8e8);
  padding: 2px 12px;
`;

const SearchButton = styled.button`
  background-color: ${(props) => (props.clicked ? "#0079E0" : "#CFD4D9")};
  color: white;
  border: ${(props) =>
    props.clicked ? "none" : "1px solid var(--admin-gray-stroke, #E8E8E8)"};
  padding: 6px 14px;
  border-radius: 5px;
  margin-left: 15px;
  font-family: "Noto Sans KR";
  font-size: 15px;
  width: 74px;
`;


const Column = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: start;
  margin: 40px 0;
`;

const AdminRegister = () => {
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState([]);
  const [queriedMemberData, setQueriedMemberData] = useState([]);
  const [isSearched, setIsSearched] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // 총학생회 소속은 항상 총학 관리자(auth 3)이므로, 일반 관리자(auth 2) 등록
  // 화면에서는 총학생회를 제외한 산하 자치기구만 선택한다.
  const [adminCategory, setAdminCategory] = useState("중선관위");
  const dropdownConditionOptions = ["이름", "이메일"];
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [selectedConditionOption, setSelectedConditionOption] = useState(
    dropdownConditionOptions[0]
  );
  const [textInput, setTextInput] = useState("");

  const sourceData = isSearched ? queriedMemberData : memberData;
  const selection = useTableSelection(sourceData);
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(sourceData, { pageSize: 9 });

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      { Header: "이름", accessor: "name" },
      { Header: "이메일", accessor: "email" },
      { Header: "가입일", accessor: "createdAt" },
      { Header: "최근 로그인", accessor: "updatedAt" },
      {
        Header: "구분",
        accessor: "category",
        Cell: () => "일반",
      },

      // { Header: "조회수", accessor: "view" },
    ],
    []
  );

  // 이전(<) 핸들러: 검색어나 선택한 사용자가 있을 때만 확인 모달을 띄운다.
  const handleButtonClick = async () => {
    const shouldLeave = await confirmLeaveIfDirty(
      Boolean(textInput || (selection && selection.count > 0)),
      "관리자 등록을 취소하시겠습니까?\n취소시 선택한 내용이 사라집니다.",
    );
    if (shouldLeave) {
      navigate("./../");
    }
  };

  const onChangeTextInput = (event) => {
    setTextInput(event.target.value);
  };

  const readMembers = async () => {
    const members = [];
    try {
      const fetchedMembers = await fetchMembers();
      fetchedMembers.forEach((member) => {
        if (member.auth === 1) {
          members.push(member);
        }
      });
      setMemberData(members.slice().reverse());
    } catch (error) {
    }
  };

  useEffect(() => {
    readMembers();
  }, []);

  const handleSearchButtonClick = () => {
    const queried = [];
    memberData.forEach((member) => {
      // Check if the member condition includes the search term
      const includesSearchTerm =
        (selectedConditionOption === dropdownConditionOptions[0] &&
          (member.name?.includes(textInput) ?? false)) ||
        (selectedConditionOption === dropdownConditionOptions[1] &&
          (member.email?.includes(textInput) ?? false));

      if (includesSearchTerm) {
        queried.push(member);
      }
    });

    if (queried.length === 0) {
      alertDialog(
        "해당 정보를 찾을 수 없습니다.\n정보를 다시 확인하시고 검색해주세요."
      );
    } else {
      setQueriedMemberData(queried);
      setIsSearched(true);
    }
  };

  const handleRegisterClick = async () => {
    const shouldProceed = await confirmDialog(
      "해당 사용자를 일반 관리자로 등록하시겠습니까?\n일반 관리자 해제는 마스터 관리자만 가능합니다."
    );

    if (!shouldProceed) return;

    try {
      // 선택한 사용자를 모두 일반 관리자(auth 2)로 등록하고, 선택한 소속(adminCategory)을
      // 함께 보내 백엔드가 Admin 행의 department로 저장하도록 한다.
      // (기존에는 루프 안에서 reload를 호출해 두 번째 이후 등록이 중단되는 버그가 있었다.)
      await Promise.all(
        selection.selectedRows.map((member) =>
          updateMemberAuth(member.id, 2, adminCategory),
        ),
      );
      setIsModalOpen(false);
      // 회원 관리 목록으로 복귀하면 그 페이지가 최신 목록을 다시 불러온다(전체 리로드 불필요).
      navigate("./../");
    } catch (error) {
      alertDialog("관리자 등록에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <Div>
      <Row>
        <svg
          onClick={handleButtonClick}
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="35"
          viewBox="0 3 35 35"
          fill="none"
          style={{ cursor: "pointer" }}
        >
          <path
            d="M22.4219 7.65625L12.5781 17.5L22.4219 27.3438"
            stroke="black"
            strokeOpacity="0.5"
            strokeWidth="3"
            strokeMiterlimit="10"
            strokeLinecap="square"
          />
        </svg>
        <h2 style={{ fontWeight: "bold" }}>관리자 추가하기</h2>
      </Row>
      <Container style={{ marginBottom: "60px" }}>
        <Row>
          <span style={{ flexBasis: "110px", fontSize: "16px", flexShrink: 0 }}>
            사용자 정보
          </span>
          <Dropdown
            options={dropdownConditionOptions}
            selectedOption={selectedConditionOption}
            isOpen={isConditionOpen}
            setIsOpen={setIsConditionOpen}
            setSelectedOption={setSelectedConditionOption}
            width={"100px"}
          />
          <TextInput
            style={{ marginLeft: "5px" }}
            value={textInput}
            onChange={onChangeTextInput}
            placeholder="검색어 입력"
          />
          <SearchButton
            onClick={
              textInput.length === 0 ? () => {} : handleSearchButtonClick
            }
            style={
              textInput.length === 0
                ? {
                    backgroundColor: "#CFD4D9",
                    border: "1px solid var(--admin-gray-stroke, #E8E8E8)",
                    cursor: "not-allowed",
                    flexShrink: 0,
                  }
                : {
                    backgroundColor: "#0079E0",
                    border: "none",
                    flexShrink: 0,
                  }
            }
          >
            검색
          </SearchButton>
        </Row>
      </Container>
      <DataTable
        columns={columns}
        data={pageItems}
        selectable
        selection={selection}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
      <ButtonBar $pb="0" style={{ marginTop: "15vh" }}>
        <ModalButton
          style={
            selection.count === 0
              ? {
                  backgroundColor: "#CFD4D9",
                  border: "1px solid var(--admin-gray-stroke, #E8E8E8)",
                  color: "white",
                  cursor: "not-allowed",
                  padding: "10px 21px",
                }
              : {
                  backgroundColor: "#0079E0",
                  color: "white",
                  border: "none",
                  padding: "10px 21px",
                }
          }
          onClick={
            selection.count === 0
              ? () => {}
              : () => {
                  setIsConditionOpen(false);
                  setIsModalOpen(true);
                }
          }
        >
          일반 관리자 등록
        </ModalButton>
        <ModalShell
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          width={450}
          height={400}
        >
          <h2 style={{ fontWeight: "bold" }}>관리자 구분</h2>
          <Column style={{ flexGrow: 1 }}>
            <ChipGroup
              vertical
              options={[
                {
                  label: "총학생회 중앙집행위원회 관리자",
                  value: "중선관위",
                },
                {
                  label: "총동아리연합회 관리자",
                  value: "총동연",
                },
                {
                  label: "자치회 관리자",
                  value: "예심위",
                },
              ]}
              value={adminCategory}
              onChange={setAdminCategory}
              allowDeselect={false}
            />
          </Column>
          <ButtonBar $pb="0">
            <ModalButton onClick={() => setIsModalOpen(false)}>
              취소
            </ModalButton>
            <ModalButton
              style={{
                backgroundColor: "#0079E0",
                color: "white",
                border: "none",
                padding: "10px 21px",
              }}
              onClick={handleRegisterClick}
            >
              일반 관리자 등록
            </ModalButton>
          </ButtonBar>
        </ModalShell>
      </ButtonBar>
    </Div>
  );
};

export default AdminRegister;
